import {BackgroundView} from '@fantasmagames/slot/src/modules/background/BackgroundView';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {DeviceType} from '@fantasmagames/core/src/device/DeviceType';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {ObjectSize, Scale} from '@fantasmagames/slot/src/modules/reels/ReelConstants';

export class GameBackgroundView extends BackgroundView {
	protected _reelsBackgroundContainer: PIXI.Sprite;

	protected _topReelCoverAnimation: SpineAnimation;

	protected _baseGameAnimation: SpineAnimation;
	protected _freeSpinsAnimation: SpineAnimation;

	protected _rowDividerAnimation: SpineAnimation;

	protected _topReelMystEffectAnimation: SpineAnimation;
	protected _topReelEffectMask: PIXI.Graphics;

	protected _isInFreeSpins: boolean = false;

	protected _currentRowDividerMask: PIXI.Graphics;

	protected setupSprites(): void {
		this._mainGameBackground = new PIXI.Sprite();
		this.container.addChild(this._mainGameBackground);

		this._freeSpinsBackground = new PIXI.Sprite();
		this.container.addChild(this._freeSpinsBackground);

		this._reelsBackgroundContainer = new PIXI.Sprite();

		if (this.device.type !== DeviceType.DESKTOP) {
			this.container.addChild(this._reelsBackgroundContainer);
		}

		this.playBaseBackGroundIdleAnimation();

		this.plaFreeSpinsBackGroundIdleAnimation();

		const reelsBackground: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'REEL_BACKGROUND.png'));
		const pos: Position = this.layout.getAbsolutePosition('REEL_BACKGROUND');
		reelsBackground.position.set(pos.x, pos.y);
		this._reelsBackgroundContainer.addChild(reelsBackground);

		this.setupIdleAnimations();
	}

	protected clearIdleAnimations(): void {
		this._topReelCoverAnimation && this.spineUtils.stopAndDestroyAnimation(this._topReelCoverAnimation);
		this._topReelMystEffectAnimation && this.spineUtils.stopAndDestroyAnimation(this._topReelMystEffectAnimation);
		this._rowDividerAnimation && this.spineUtils.stopAndDestroyAnimation(this._rowDividerAnimation);
	}

	public setupIdleAnimations(): void {
		this.clearIdleAnimations();

		const idleAnimationName: string = this._isInFreeSpins ? 'IDLE_FREESPIN' : 'IDLE';

		this._topReelCoverAnimation =
			this.spineUtils.startSpineAnimationCreation('TOP_REEL.json', idleAnimationName, true)
				.position(this.layout.getAbsolutePosition('TOP_REEL_EFFECT'))
				.createAndStart();

		this._topReelMystEffectAnimation =
			this.spineUtils.startSpineAnimationCreation('TOP_REEL_EFFECT.json', idleAnimationName, true)
				.position(this.layout.getAbsolutePosition('TOP_REEL_EFFECT'))
				.createAndStart();

		this._rowDividerAnimation =
			this.spineUtils.startSpineAnimationCreation('ROW_DIVIDER.json', idleAnimationName, true)
				.position(this.layout.getAbsolutePosition(`ROW_DIVIDER_03`))
				.createAndStart();

		if (this.device.type === DeviceType.DESKTOP) {
			this.addReelFrames();
			this.createMaskForReelEffect();
		}
		this.createMaskForRowDivider();
	}

	protected createMaskForRowDivider(): void {
		this._currentRowDividerMask = new PIXI.Graphics();

		const scale: Scale = this.layout.getScale('BOTTOM_REEL_MASK');
		const noScaleSize: ObjectSize = this.layout.getSize('BOTTOM_REEL_MASK');
		const maskSize: ObjectSize = {width: noScaleSize.width * scale.x, height: noScaleSize.height * scale.y};
		const maskPos: Position = this.layout.getAbsolutePosition('BOTTOM_REEL_MASK');

		this._currentRowDividerMask.drawRect(0, 0, 1, 1);

		this._currentRowDividerMask.x = maskPos.x - maskSize.width / 2;
		this._currentRowDividerMask.y = maskPos.y - maskSize.height / 2 - 150;
		this._currentRowDividerMask.width = maskSize.width;
		this._currentRowDividerMask.height = 150;

		this.container.addChild(this._currentRowDividerMask);

		this._rowDividerAnimation.container.mask = this._currentRowDividerMask;
	}

	protected createMaskForReelEffect(): void {
		this._topReelEffectMask = new PIXI.Graphics();

		const scale: Scale = this.layout.getScale('TOP_REEL_MASK');
		const noScaleSize: ObjectSize = this.layout.getSize('TOP_REEL_MASK');
		const maskSize: ObjectSize = {width: noScaleSize.width * scale.x, height: noScaleSize.height * scale.y};
		const maskPos: Position = this.layout.getAbsolutePosition('TOP_REEL_MASK');

		this._topReelEffectMask.drawRect(0, 0, 1, 1);

		this._topReelEffectMask.x = maskPos.x - maskSize.width / 2;
		this._topReelEffectMask.y = maskPos.y - maskSize.height / 2 - 100;
		this._topReelEffectMask.width = maskSize.width;
		this._topReelEffectMask.height = maskSize.height + 100;

		this.container.addChild(this._topReelEffectMask);

		this._topReelMystEffectAnimation.container.mask = this._topReelEffectMask;
		this._topReelCoverAnimation.container.mask = this._topReelEffectMask;
	}

	protected addReelFrames(): void {
		const leftFrame: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'REEL_FRAME.png'));
		leftFrame.anchor.set(0.5);
		const leftFramePos: Position = this.layout.getAbsolutePosition('REEL_FRAME_LEFT');
		leftFrame.position.set(leftFramePos.x, leftFramePos.y);
		this._reelsBackgroundContainer.addChild(leftFrame);

		const rightFrame: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'REEL_FRAME.png'));
		rightFrame.anchor.set(0.5);
		const rightFramePos: Position = this.layout.getAbsolutePosition('REEL_FRAME_RIGHT');
		rightFrame.position.set(rightFramePos.x, rightFramePos.y);
		this._reelsBackgroundContainer.addChild(rightFrame);

		this.container.addChild(this._reelsBackgroundContainer);
	}

	protected playBaseBackGroundIdleAnimation(): void {
		this._baseGameAnimation = this.spineUtils.startSpineAnimationCreation('BASEGAME_BACKGROUND.json', 'IDLE', true)
			.container(this._mainGameBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.createAndStart();
	}

	protected plaFreeSpinsBackGroundIdleAnimation(): void {
		this._freeSpinsAnimation = this.spineUtils.startSpineAnimationCreation('FREESPINS_BACKGROUND.json', 'IDLE', true)
			.container(this._freeSpinsBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.createAndStart();
	}

	public playStartAnimation(): void {
		this.cleanBaseBackground();

		this._baseGameAnimation = this.spineUtils.startSpineAnimationCreation('BASEGAME_BACKGROUND.json', 'GAME_START', false)
			.container(this._mainGameBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.chainIdleAnimation('IDLE')
			.createAndStart();
	}

	public playEnterFreeSpinsAnimation(): void {
		this.cleanFreeSpinsBackground();
		this._freeSpinsAnimation = this.spineUtils.startSpineAnimationCreation('FREESPINS_BACKGROUND.json', 'ENTER_FREESPINS', false)
			.container(this._freeSpinsBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.chainIdleAnimation('IDLE')
			.createAndStart();

		this.cleanBaseBackground();
		this._baseGameAnimation = this.spineUtils.startSpineAnimationCreation('BASEGAME_BACKGROUND.json', 'ENTER_FREESPINS', false)
			.container(this._mainGameBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.createAndStart();
	}

	public playExitFreeSpinsAnimation(): void {
		this.cleanFreeSpinsBackground();
		this._freeSpinsAnimation = this.spineUtils.startSpineAnimationCreation('FREESPINS_BACKGROUND.json', 'EXIT_FREESPINS', false)
			.container(this._freeSpinsBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.createAndStart();

		this.cleanBaseBackground();
		this._baseGameAnimation = this.spineUtils.startSpineAnimationCreation('BASEGAME_BACKGROUND.json', 'ENTER_FREESPINS', false)
			.container(this._mainGameBackground)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.chainIdleAnimation('IDLE')
			.createAndStart();
	}

	protected cleanBaseBackground(): void {
		if (this._baseGameAnimation) {
			this.spineUtils.stopAndDestroyAnimation(this._baseGameAnimation);
			this._baseGameAnimation = null;
		}
	}

	protected cleanFreeSpinsBackground(): void {
		if (this._freeSpinsAnimation) {
			this.spineUtils.stopAndDestroyAnimation(this._freeSpinsAnimation);
			this._freeSpinsAnimation = null;
		}
	}

	protected initBackgrounds(): void {
		this.showMainGameBackground();

		this.emit(BackgroundView.BACKGROUND_CREATED);
	}

	public startTransition(): void {
		this.playTransition(this._reelsBackgroundContainer);
		this.playTransition(this._topReelCoverAnimation.container);
		this.playTransition(this._rowDividerAnimation.container);
		this.playTransition(this._topReelMystEffectAnimation.container);
	}

	protected playTransition(container: PIXI.Container): void {
		const appearTween: Tween = new Tween(container)
			.to({alpha: 1.0}, BackgroundView.TRANSFER_TIME)
			.start();

		this.updateManager.add(appearTween);
	}

	public setReelsBackgroundAlphaToZero(): void {
		this._reelsBackgroundContainer.alpha = 0;
		this._topReelCoverAnimation.container.alpha = 0;
		this._rowDividerAnimation.container.alpha = 0;
		this._topReelMystEffectAnimation.container.alpha = 0;
	}

	public hideReelBackground(): void {
		this._reelsBackgroundContainer.visible = false;
		this._topReelCoverAnimation.container.visible = false;
		this._rowDividerAnimation.container.visible = false;
		this._topReelMystEffectAnimation.container.visible = false;
	}

	public showReelBackground(): void {
		this._reelsBackgroundContainer.visible = true;
		this._topReelCoverAnimation.container.visible = true;
		this._rowDividerAnimation.container.visible = true;
		this._topReelMystEffectAnimation.container.visible = true;
	}

	public set isInFreeSpins(value: boolean) {
		this._isInFreeSpins = value;
	}
}



// WEBPACK FOOTER //
// ./src/background/GameBackgroundView.ts